/**
 * Asiakasvärit (suunnitelma 10, "Asiakasvärit").
 *
 * Sävy kertoo, **kenelle** työ on tehty. Se ei kerro tilaa: laskutettavuus,
 * lukitus ja budjetin ylitys pysyvät merkkeinä ja teksteinä (15). Ero
 * pidetään siinä, kummalla puolella väri on — tilaväri on tekstin ja
 * kuvakkeen väri, asiakasväri on tekstin **tausta**. Siksi asiakaskoodi on
 * luettavissa sävystä riippumatta eikä kontrasti ole sävyn varassa, ja
 * siksi punainen asiakas ei tarkoita ylitystä eikä vihreä kunnossa olevaa.
 *
 * Paletti on jaetussa paketissa eikä selaimessa, koska palvelin validoi
 * `Customer.colorKey`n samasta luettelosta, jonka mukaan selain piirtää.
 * Kaksi kopiota eriytyisi, ja silloin kantaan pääsisi avain, jota mikään
 * ei osaa piirtää.
 *
 * Piirtämisen kirkkaus- ja värikylläisyysvakiot ovat `apps/web/src/styles.css`:ssä
 * teemakohtaisina — täällä on vain se, mikä ei riipu teemasta.
 */
/**
 * Sävyt OKLCH-kehällä. Vain **kulma** vaihtuu asiakkaittain; kirkkaus ja
 * värikylläisyys ovat teemakohtaisia vakioita. Siksi kaikki sävyt ovat
 * yhtä vaaleita, mikään ei huuda muita kovemmin, ja kontrasti tekstiväriä
 * vastaan on jokaisella sama: se mitataan kerran per teema eikä kymmentä
 * kertaa.
 *
 * Kehältä puuttuu väli 235°–265°, jossa oli alkuperäinen sininen
 * `--color-korostus`. Korostus on nyt Devikoneen punainen, mutta väliä ei
 * ole siirretty: avaimet ovat kannassa, ja kehän muuttaminen vaihtaisi
 * vain asiakkaiden värit. `punainen` (25°) on brändin sävyä, mutta sirun
 * taustana se on pastelli eikä sekoitu kylläiseen korostukseen.
 *
 * Kymmenen eikä kahtakymmentä: useampaa pientä värilaikkua ei erota
 * kukaan. Siitä seuraa, että sävyt törmäävät — se on hyväksytty, koska
 * värin tehtävä on ryhmitellä eikä tunnistaa, ja tunnistuksen tekee koodi,
 * joka on aina vieressä.
 *
 * **Avain on pysyvä.** Se tallentuu kantaan `customer.color_key`-sarakkeeseen
 * ja näkyy muutoslokissa. Avaimen nimeäminen uudelleen tarkoittaisi
 * migraatiota, joten sävyn nimen muuttaminen ei ole sanavalinta vaan
 * skeemamuutos.
 */
export const SAVYT = [
    { avain: 'punainen', kulma: 25 },
    { avain: 'oranssi', kulma: 55 },
    { avain: 'kulta', kulma: 85 },
    { avain: 'vihrea', kulma: 120 },
    { avain: 'smaragdi', kulma: 155 },
    { avain: 'turkoosi', kulma: 190 },
    { avain: 'taivas', kulma: 220 },
    { avain: 'violetti', kulma: 285 },
    { avain: 'purppura', kulma: 315 },
    { avain: 'pinkki', kulma: 350 },
];
export const SAVY_AVAIMET = SAVYT.map((savy) => savy.avain);
export function onSavyAvain(arvo) {
    return typeof arvo === 'string' && SAVY_AVAIMET.includes(arvo);
}
/**
 * FNV-1a, 32 bittiä.
 *
 * Tiiviste eikä koodin merkkien summa: summa antaa anagrammeille saman
 * arvon, ja asiakaskoodit ovat lyhyitä ja samankaltaisia juuri siksi, että
 * ne ovat saman yrityksen nimistä johdettuja lyhenteitä.
 *
 * `Math.imul` eikä `*`: kertolasku vuotaa liukuluvuksi jo muutaman
 * merkin jälkeen, jolloin tulos riippuisi siitä, missä se lasketaan.
 */
function tiiviste(teksti) {
    let arvo = 0x811c9dc5;
    for (let i = 0; i < teksti.length; i += 1) {
        arvo ^= teksti.charCodeAt(i);
        arvo = Math.imul(arvo, 0x01000193);
    }
    return arvo >>> 0;
}
/**
 * Asiakkaan sävy: valittu, jos sellainen on, muuten koodista johdettu.
 *
 * Johdettu on oletus eikä valittu, koska asiakkaita on kymmeniä ja niitä
 * perustetaan lisää: käsin valittava väri jäisi puolelta antamatta, ja
 * puoliksi väritetty kalenteri näyttää rikkinäiseltä. Valinta on
 * poikkeus, jolla puretaan ne törmäykset, jotka oikeasti häiritsevät.
 *
 * Koodista eikä id:stä: koodi on ihmisen näkemä tunniste, ja koodista
 * johdettu sävy on sama kehitys-, testi- ja tuotantokannassa.
 *
 * **Tuntematon avain putoaa johdettuun.** Kannassa voi olla avain, jonka
 * paletista on poistettu — silloin asiakas saa värin, joka on olemassa,
 * eikä näkymä hajoa. Tämä on parempi takuu kuin kannan CHECK-rajoite,
 * joka estäisi paletin muuttamisen ilman migraatiota.
 */
export function savyAvain(asiakasKoodi, valittu) {
    if (onSavyAvain(valittu))
        return valittu;
    // Jakojäännös on aina taulukon sisällä, mutta tyyppi ei tiedä sitä
    // eikä tästä kannata heittää.
    return SAVYT[tiiviste(asiakasKoodi) % SAVYT.length]?.avain ?? SAVYT[0].avain;
}
/** Asiakkaan sävykulma asteina — se, mikä menee CSS:lle. */
export function savyKulma(asiakasKoodi, valittu) {
    const avain = savyAvain(asiakasKoodi, valittu);
    return SAVYT.find((savy) => savy.avain === avain)?.kulma ?? SAVYT[0].kulma;
}
//# sourceMappingURL=varit.js.map